/**
 * NewLeadWizard — one modal, three steps: Contact → Property → Deal, all
 * pre-linked. Replaces the 3-page create flow for new leads. Opened from the
 * "+ New Lead" button at the top of the sidebar.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { User, Building2, Briefcase, ArrowRight, ArrowLeft, Check, Loader2 } from "lucide-react";

const F = "w-full h-10 px-3 border border-zinc-300 rounded-sm text-sm";
const L = "block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1";
const CONTACT_TYPES = ["Owner", "Property Manager", "CEO", "COO", "CFO", "Tenant", "Other"];

const STEPS = [
  { icon: User, label: "Contact" },
  { icon: Building2, label: "Property" },
  { icon: Briefcase, label: "Deal" },
];

export default function NewLeadWizard({ open, onOpenChange }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Step 1 — contact (new or existing)
  const [mode, setMode] = useState("new"); // new | existing
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [existingContactId, setExistingContactId] = useState("");
  const [contact, setContact] = useState({ contact_name: "", company_name: "", contact_type: "Owner", email: "", phone: "" });

  // Step 2 — property
  const [property, setProperty] = useState({
    property_name: "", property_address: "", property_city: "", property_state: "", property_zip: "",
    roof_sqft: "", parapet_wall_sqft: "", stories: "", roof_access: "",
  });

  // Step 3 — deal
  const [deal, setDeal] = useState({ title: "", project_type: "Repair", notes: "" });

  useEffect(() => {
    if (!open) return;
    setStep(0); setMode("new"); setExistingContactId(""); setContactSearch("");
    setContact({ contact_name: "", company_name: "", contact_type: "Owner", email: "", phone: "" });
    setProperty({ property_name: "", property_address: "", property_city: "", property_state: "", property_zip: "", roof_sqft: "", parapet_wall_sqft: "", stories: "", roof_access: "" });
    setDeal({ title: "", project_type: "Repair", notes: "" });
    api.get("/contacts").then((r) => setContacts(r.data || [])).catch(() => {});
  }, [open]);

  const filteredContacts = useMemo(() => {
    const q = contactSearch.toLowerCase();
    if (!q) return contacts.slice(0, 8);
    return contacts.filter((c) =>
      (c.contact_name || "").toLowerCase().includes(q) ||
      (c.company_name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q)
    ).slice(0, 8);
  }, [contacts, contactSearch]);

  const selectedExisting = contacts.find((c) => c.id === existingContactId);
  const contactLabel = mode === "existing"
    ? (selectedExisting ? selectedExisting.contact_name : "")
    : contact.contact_name;

  const step1Valid = mode === "existing" ? !!existingContactId : !!contact.contact_name.trim();
  const step2Valid = !!property.property_name.trim();
  const step3Valid = !!deal.title.trim();

  const goNext = () => {
    if (step === 0) {
      // Prefill property name + deal title from what we know
      if (!property.property_name) {
        const base = mode === "existing"
          ? (selectedExisting?.company_name || selectedExisting?.contact_name || "")
          : (contact.company_name || contact.contact_name || "");
        setProperty((p) => ({ ...p, property_name: base ? `${base} Building` : "" }));
      }
    }
    if (step === 1 && !deal.title) {
      setDeal((d) => ({ ...d, title: property.property_name || "" }));
    }
    setStep((s) => s + 1);
  };

  const finish = async () => {
    setSaving(true);
    try {
      // 1. Contact — create or reuse
      let contactId = existingContactId;
      let contactName = selectedExisting?.contact_name || "";
      if (mode === "new") {
        const cr = await api.post("/contacts", contact);
        contactId = cr.data.id;
        contactName = cr.data.contact_name;
      }
      // 2. Property — linked to contact
      const pr = await api.post("/properties", {
        ...property,
        roof_sqft: parseFloat(property.roof_sqft) || 0,
        parapet_wall_sqft: parseFloat(property.parapet_wall_sqft) || 0,
        property_contact_id: contactId,
        property_contact_name: contactName,
      });
      // 3. Deal — linked to both, assigned to me
      const dr = await api.post("/deals", {
        title: deal.title,
        project_type: deal.project_type,
        notes: deal.notes,
        status: "Lead",
        contact_id: contactId,
        customer_contact_id: contactId,
        property_id: pr.data.id,
        assigned_to_user_id: user?.id || null,
      });
      toast.success("Lead created — contact, property and deal all linked");
      onOpenChange(false);
      nav(`/deals/${dr.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-lead-wizard">
        <DialogHeader>
          <DialogTitle>New Lead</DialogTitle>
          <DialogDescription>Contact → Property → Deal, linked automatically. Under a minute.</DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.label}>
              {i > 0 && <div className={`flex-1 h-px ${i <= step ? "bg-emerald-600" : "bg-zinc-200"}`} />}
              <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${i === step ? "text-emerald-700" : i < step ? "text-zinc-950" : "text-zinc-400"}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center border ${i < step ? "bg-emerald-600 border-emerald-600 text-white" : i === step ? "border-emerald-600" : "border-zinc-300"}`}>
                  {i < step ? <Check className="w-3.5 h-3.5" /> : <s.icon className="w-3 h-3" />}
                </span>
                {s.label}
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Step 1 — Contact */}
        {step === 0 && (
          <div className="space-y-3" data-testid="wizard-step-contact">
            <div className="inline-flex border border-zinc-300 rounded-sm overflow-hidden">
              {[["new", "New Contact"], ["existing", "Existing Contact"]].map(([k, label]) => (
                <button key={k} onClick={() => setMode(k)} data-testid={`wizard-contact-mode-${k}`}
                  className={`h-8 px-3 text-[10px] font-bold uppercase tracking-wider ${mode === k ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-100"}`}>
                  {label}
                </button>
              ))}
            </div>
            {mode === "existing" ? (
              <div>
                <label className={L}>Search Contacts</label>
                <input value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Name, company, or email…" data-testid="wizard-contact-search" className={F} />
                <div className="mt-2 border border-zinc-200 rounded-sm divide-y divide-zinc-100 max-h-44 overflow-y-auto">
                  {filteredContacts.map((c) => (
                    <button key={c.id} onClick={() => setExistingContactId(c.id)} data-testid={`wizard-contact-pick-${c.id}`}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 ${existingContactId === c.id ? "bg-emerald-50 border-l-2 border-emerald-600" : ""}`}>
                      <span className="font-bold">{c.contact_name}</span>
                      <span className="text-zinc-500 text-xs ml-2">{c.company_name || c.email}</span>
                    </button>
                  ))}
                  {filteredContacts.length === 0 && <div className="px-3 py-3 text-xs text-zinc-500">No matches — switch to New Contact.</div>}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <label className={L}>Full Name *</label>
                  <input value={contact.contact_name} onChange={(e) => setContact({ ...contact, contact_name: e.target.value })} data-testid="wizard-contact-name" className={F} />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className={L}>Company</label>
                  <input value={contact.company_name} onChange={(e) => setContact({ ...contact, company_name: e.target.value })} data-testid="wizard-contact-company" className={F} />
                </div>
                <div>
                  <label className={L}>Type</label>
                  <select value={contact.contact_type} onChange={(e) => setContact({ ...contact, contact_type: e.target.value })} data-testid="wizard-contact-type" className={F}>
                    {CONTACT_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={L}>Phone</label>
                  <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="wizard-contact-phone" className={F} />
                </div>
                <div className="col-span-2">
                  <label className={L}>Email</label>
                  <input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="wizard-contact-email" className={F} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2 — Property */}
        {step === 1 && (
          <div className="grid grid-cols-2 gap-3" data-testid="wizard-step-property">
            <div className="col-span-2">
              <label className={L}>Property Name *</label>
              <input value={property.property_name} onChange={(e) => setProperty({ ...property, property_name: e.target.value })} data-testid="wizard-property-name" className={F} />
            </div>
            <div className="col-span-2">
              <label className={L}>Street Address</label>
              <input value={property.property_address} onChange={(e) => setProperty({ ...property, property_address: e.target.value })} data-testid="wizard-property-address" className={F} />
            </div>
            <div>
              <label className={L}>City</label>
              <input value={property.property_city} onChange={(e) => setProperty({ ...property, property_city: e.target.value })} data-testid="wizard-property-city" className={F} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={L}>State</label>
                <input value={property.property_state} onChange={(e) => setProperty({ ...property, property_state: e.target.value })} data-testid="wizard-property-state" className={F} />
              </div>
              <div>
                <label className={L}>ZIP</label>
                <input value={property.property_zip} onChange={(e) => setProperty({ ...property, property_zip: e.target.value })} data-testid="wizard-property-zip" className={F} />
              </div>
            </div>
            <div>
              <label className={L}>Roof SF</label>
              <input type="number" min="0" value={property.roof_sqft} onChange={(e) => setProperty({ ...property, roof_sqft: e.target.value })} data-testid="wizard-property-roofsf" className={F} />
            </div>
            <div>
              <label className={L}>Parapet Wall SF</label>
              <input type="number" min="0" value={property.parapet_wall_sqft} onChange={(e) => setProperty({ ...property, parapet_wall_sqft: e.target.value })} data-testid="wizard-property-parapetsf" className={F} />
            </div>
            <div>
              <label className={L}>Stories</label>
              <input value={property.stories} onChange={(e) => setProperty({ ...property, stories: e.target.value })} placeholder="e.g. 2" data-testid="wizard-property-stories" className={F} />
            </div>
            <div>
              <label className={L}>Roof Access</label>
              <input value={property.roof_access} onChange={(e) => setProperty({ ...property, roof_access: e.target.value })} placeholder="e.g. Interior hatch" data-testid="wizard-property-access" className={F} />
            </div>
            <div className="col-span-2 text-[11px] text-zinc-500">Measurements entered here auto-fill the deal, calculator and every PDF — no re-typing later.</div>
          </div>
        )}

        {/* Step 3 — Deal */}
        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-deal">
            <div>
              <label className={L}>Deal Title *</label>
              <input value={deal.title} onChange={(e) => setDeal({ ...deal, title: e.target.value })} data-testid="wizard-deal-title" className={F} />
            </div>
            <div>
              <label className={L}>Notes (optional)</label>
              <textarea value={deal.notes} onChange={(e) => setDeal({ ...deal, notes: e.target.value })} rows={3} data-testid="wizard-deal-notes" className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm" />
            </div>
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-xs text-zinc-600 space-y-1">
              <div><b>Contact:</b> {contactLabel || "—"}</div>
              <div><b>Property:</b> {property.property_name || "—"}{property.roof_sqft ? ` · ${Number(property.roof_sqft).toLocaleString()} SF roof` : ""}</div>
              <div><b>Stage:</b> Lead · assigned to you</div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between pt-2">
          <button onClick={() => (step === 0 ? onOpenChange(false) : setStep(step - 1))} data-testid="wizard-back"
            className="h-10 px-4 text-xs font-bold uppercase tracking-wider text-zinc-600 hover:bg-zinc-100 rounded-sm inline-flex items-center gap-1.5">
            {step === 0 ? "Cancel" : <><ArrowLeft className="w-3.5 h-3.5" /> Back</>}
          </button>
          {step < 2 ? (
            <button onClick={goNext} disabled={step === 0 ? !step1Valid : !step2Valid} data-testid="wizard-next"
              className="h-10 px-5 text-xs font-bold uppercase tracking-wider bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm disabled:opacity-40 inline-flex items-center gap-1.5">
              Next <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button onClick={finish} disabled={!step3Valid || saving} data-testid="wizard-finish"
              className="h-10 px-5 text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm disabled:opacity-40 inline-flex items-center gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Create Lead
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
